import { useState } from "react";
import { Mic2, Sparkles, ShieldCheck, Layers3, Scissors, SlidersHorizontal, Bookmark, Plus, Trash2, Star, Circle, Eraser, Zap, X, Music, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { EFFECT_LIST, STEMS_LIST, padCh } from "./dawHelpers";
import Knob from "./Knob";
import { FX_PARAM_SCHEMA, FX_PARAM_DEFAULTS } from "@/lib/audioEngine";
import { getAllPresets, addUserPreset, deleteUserPreset } from "@/lib/fxPresets";

/* ─────────────────────────────────────────────────────────────────────────
 *  Per-param automation lane — tiny inline sparkline.
 *
 *  Renders the recorded curve for one (effect, param) pair as a single
 *  SVG path. We downsample to MAX_SAMPLES to keep the DOM cheap even when
 *  a knob has been twisted aggressively for minutes (lanes can hit thousands
 *  of points).
 *  ───────────────────────────────────────────────────────────────────────── */
const MAX_SAMPLES = 64;

function LaneSparkline({ points, min, max }) {
  if (!points || points.length === 0) return null;
  // Downsample so the path stays performant.
  const step = Math.max(1, Math.ceil(points.length / MAX_SAMPLES));
  const sampled = [];
  for (let i = 0; i < points.length; i += step) sampled.push(points[i]);
  if (sampled[sampled.length - 1] !== points[points.length - 1]) sampled.push(points[points.length - 1]);
  // Time-normalise to [0,1] horizontally; value-normalise vertically.
  const t0 = sampled[0].t;
  const tN = sampled[sampled.length - 1].t;
  const tSpan = Math.max(0.001, tN - t0);
  const vSpan = Math.max(0.001, max - min);
  // 80×16 viewBox — matches the visual budget alongside the param name.
  const W = 80, H = 16;
  const d = sampled.map((p, i) => {
    const x = ((p.t - t0) / tSpan) * W;
    // Invert Y so high values render at top.
    const y = H - ((p.v - min) / vSpan) * H;
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");
  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="flex-shrink-0 overflow-visible"
      aria-hidden="true"
    >
      <path d={d} stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


export function VoiceCloneCard({ onOpen, voiceCount }) {
  return (
    <Card className="glass-card rounded-3xl border-0 text-white">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-fuchsia-300 drop-shadow-[0_0_10px_rgba(232,121,249,0.6)]" />
            <h2 className="font-display text-xl font-semibold tracking-tight">AI Voice Clone</h2>
          </div>
          {voiceCount > 0 && (
            <span className="rounded-full bg-fuchsia-500/20 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-fuchsia-200">
              {voiceCount} cloned
            </span>
          )}
        </div>
        <p className="mb-4 text-[13px] leading-6 text-white/60">
          Clone a voice you own (or have written consent for) with ElevenLabs, then speak any text — including your lyrics — in that voice and route it straight to a channel.
        </p>
        <Button
          data-testid="voice-clone-open-btn"
          onClick={onOpen}
          className="w-full rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 text-white hover:opacity-90"
        >
          <Mic2 className="mr-2 h-4 w-4" /> Open voice studio
        </Button>
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] px-3 py-2 text-[11px] text-emerald-200/80">
          <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0" />
          Consent acknowledgment is required before any clone is created.
        </div>
      </CardContent>
    </Card>
  );
}

export function StemExtractorCard({ selectedChannel, selectedChannelHasAudio = true, firstChannelWithAudio = -1, onExtract, onLoadAudio, onDropAudio, busy, lastExtractionCount = 0, lastExtractionSongName = "", onDownloadPack, onPublishPack }) {
  // iter-166 — Show the real source channel that will be extracted. If
  // the selected channel is empty but another channel has audio, we
  // auto-fall-back to that channel and tell the user up front instead
  // of silently refusing.
  const effectiveSourceIdx = selectedChannelHasAudio ? selectedChannel : firstChannelWithAudio;
  const noAudioAnywhere = !selectedChannelHasAudio && firstChannelWithAudio === -1;
  const usingFallback = !selectedChannelHasAudio && firstChannelWithAudio !== -1;
  // iter-217 — Drag-and-drop audio onto the card. State tracks active
  // dragover so we can paint a glowing dashed border instead of the
  // default browser preview, and we accept ONLY audio MIME types to
  // avoid hijacking unrelated drags (e.g. images, text).
  const [dragOver, setDragOver] = useState(false);
  const onDragOver = (e) => {
    if (!onDropAudio) return;
    // Detect whether the drag carries a file (vs internal DAW drags like
    // a channel-strip ribbon). e.dataTransfer.types includes "Files"
    // when an external file is being dragged.
    const hasFile = e.dataTransfer?.types?.includes?.("Files");
    if (!hasFile) return;
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };
  const onDragLeave = (e) => {
    // Filter spurious dragLeave events that fire when crossing child
    // elements — only clear if we're truly leaving the card.
    if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(false);
    }
  };
  const onDropHandler = (e) => {
    if (!onDropAudio) return;
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    // iter-218 — batch mode: pass the full Array<File>. Single-file drops
    // still produce a 1-element array so the handler is uniform.
    onDropAudio(files);
  };
  return (
    <Card
      className={`glass-card rounded-3xl border-0 text-white transition ${
        dragOver ? "ring-2 ring-sky-300/80 ring-offset-2 ring-offset-black shadow-[0_0_48px_rgba(56,189,248,0.45)]" : ""
      }`}
      data-testid="stem-extractor-card"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropHandler}
    >
      <CardContent className="p-4">
        <div className="mb-3 flex items-center gap-2">
          <Layers3 className="h-5 w-5 text-sky-300 drop-shadow-[0_0_10px_rgba(56,189,248,0.55)]" />
          <h2 className="font-display text-xl font-semibold tracking-tight">Stem Extractor</h2>
          {dragOver && (
            <span className="ml-auto rounded-md bg-sky-500/30 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-sky-100 ring-1 ring-sky-300/60">
              Drop to extract
            </span>
          )}
        </div>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-white/45">
          Target · {noAudioAnywhere
            ? <span className="text-rose-300">No audio loaded</span>
            : <>CH {padCh(effectiveSourceIdx)}{usingFallback && <span className="text-amber-300"> · auto-picked</span>}</>}
        </p>
        <div className="grid grid-cols-3 gap-1.5 text-[11px]">
          {STEMS_LIST.map((s) => (
            <div key={`stem-${s}`} className="rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 text-center font-mono uppercase tracking-wider text-white/70">{s}</div>
          ))}
        </div>
        {/* iter-220 — Make it crystal clear these chips are a PREVIEW
            of what Demucs will produce, not interactive buttons. The
            old 12-chip preview promised stems we never delivered
            (Choir/Strings/FX/Lead/BGV/Perc/Master are NOT Demucs
            outputs) and users tapped them expecting per-stem
            extraction, then thought the feature was broken. Showing
            the real 6-stem set + a label leaves no room for that
            misread. */}
        <p className="mt-2 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
          Preview · 6 stems from Demucs 6s
        </p>
        {noAudioAnywhere && (
          <p data-testid="stem-no-audio-hint" className="mt-3 rounded-lg border border-rose-400/25 bg-rose-500/[0.08] px-3 py-2 font-mono text-[10px] leading-relaxed text-rose-100">
            Load audio onto a channel first (upload an MP3 / WAV, record a track, or use Make Music) — then click Extract Stems.
            {onDropAudio && (
              <> Or just <span className="font-semibold text-sky-200">drag one or more songs here</span> to extract them in a single drop.</>
            )}
          </p>
        )}
        {/* iter-215 — When no audio is loaded, the Extract button stays
            grey + clickable as a "Load a song" CTA so the user is never
            stuck staring at a disabled button wondering why nothing
            happens. When audio IS loaded, the button is the real
            Extract trigger. */}
        {noAudioAnywhere ? (
          <Button
            data-testid="stem-extract-btn"
            onClick={onLoadAudio}
            className="mt-4 w-full rounded-2xl bg-gradient-to-r from-sky-500 to-violet-500 text-white hover:brightness-110"
          >
            <Music className="mr-2 h-4 w-4" />Open My Songs → load a track first
          </Button>
        ) : (
          <Button
            data-testid="stem-extract-btn"
            onClick={onExtract}
            className="mt-4 w-full rounded-2xl bg-white text-black hover:bg-white/90"
            disabled={busy}
          >
            <Scissors className="mr-2 h-4 w-4" />{busy ? "Extracting…" : "Extract 6 Stems"}
          </Button>
        )}
        {/* iter-252 — Once an extraction has succeeded, surface a
            re-pack/download affordance so the user can take the stems
            out of the browser. Hidden until `lastExtractionCount > 0`
            so it doesn't clutter the empty / pre-extraction state. */}
        {lastExtractionCount > 0 && onDownloadPack && (
          <button
            type="button"
            data-testid="stem-download-pack-btn"
            onClick={onDownloadPack}
            disabled={busy}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-100 transition hover:border-emerald-300/70 hover:bg-emerald-500/20 hover:text-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Download last {lastExtractionCount}-stem pack
            {lastExtractionSongName && (
              <span className="hidden truncate text-emerald-200/60 lg:inline">
                · {lastExtractionSongName}
              </span>
            )}
          </button>
        )}
        {/* iter-261 — Publish to Stems Marketplace. Visible only after
            a successful extraction so the empty state stays clean.
            Click opens the publish modal in the parent (signed-in
            gating + form happen there). */}
        {lastExtractionCount > 0 && onPublishPack && (
          <button
            type="button"
            data-testid="stem-publish-pack-btn"
            onClick={onPublishPack}
            disabled={busy}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-fuchsia-400/40 bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-fuchsia-100 transition hover:border-fuchsia-300/70 hover:from-fuchsia-500/20 hover:to-violet-500/20 hover:text-fuchsia-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Publish to marketplace
          </button>
        )}
      </CardContent>
    </Card>
  );
}

export function EffectsRackCard({
  selectedChannel,
  selectedFx,
  fxParams,
  onToggle,
  onParamChange,
  onLoadPreset,
  // ─── Automation (iter 32) ───
  automationArmed = false,
  automationSummary = null,    // {totalPoints, params:[{name,key,count}]}
  automationLanes = [],         // iter 49 — [{name, key, points:[{t,v}], min, max, count}]
  enginePlaying = false,        // drives the pulsing red dot when actively recording
  onToggleAutomationArm,
  onClearAutomation,
  onClearLaneParam,             // iter 49 — (name, key) => clearChannelAutomation(idx, name, key)
  // ─── Sidechain (iter 34) ───
  // `sidechainState` is the current channel's sidechain config (from engine.getSidechain).
  // `channelsWithAudio` is the list of {idx, name} for the dropdown; only loaded channels appear.
  sidechainState = null,
  channelsWithAudio = [],
  onSidechainChange,
}) {
  const activeCount = Object.values(selectedFx).filter(Boolean).length;
  // Local preset state — kept in component so the popover always sees the
  // latest list after a save/delete without prop drilling through ELPMStudioDAW.
  const [presets, setPresets] = useState(() => getAllPresets());
  const [newName, setNewName] = useState("");
  const refreshPresets = () => setPresets(getAllPresets());

  const handleSavePreset = () => {
    const name = newName.trim() || `My preset ${new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    const saved = addUserPreset(name, fxParams);
    setNewName("");
    refreshPresets();
    toast.success(`Saved "${saved.name}"`, { description: "Available in Presets ▾ on any channel." });
  };

  const handleDeletePreset = (id, name) => {
    if (deleteUserPreset(id)) {
      refreshPresets();
      toast.message(`Deleted "${name}"`);
    }
  };

  return (
    <Card className="glass-card rounded-3xl border-0 text-white">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-amber-300 drop-shadow-[0_0_10px_rgba(252,211,77,0.55)]" />
            <h2 className="font-display text-xl font-semibold tracking-tight">Effects Rack</h2>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-white/[0.06] px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white/70">
              {activeCount} active
            </span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  data-testid="fx-presets-btn"
                  size="sm"
                  variant="secondary"
                  className="h-7 rounded-lg border border-fuchsia-400/30 bg-fuchsia-500/15 px-2 text-[11px] font-semibold text-fuchsia-100 hover:bg-fuchsia-500/25"
                >
                  <Bookmark className="mr-1 h-3 w-3" /> Presets
                </Button>
              </PopoverTrigger>
              <PopoverContent
                data-testid="fx-presets-popover"
                align="end"
                className="w-72 rounded-2xl border-white/10 bg-[#0c0f1e]/95 p-2 text-white backdrop-blur-xl"
              >
                <div className="mb-2 flex items-center gap-1.5 px-1 pt-1 pb-2">
                  <Input
                    data-testid="fx-preset-name-input"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value.slice(0, 40))}
                    placeholder="Name this preset…"
                    className="h-7 rounded-lg border-white/10 bg-black/40 text-[12px] text-white placeholder:text-white/30 focus:border-fuchsia-400/60"
                  />
                  <Button
                    data-testid="fx-preset-save-btn"
                    size="sm"
                    onClick={handleSavePreset}
                    className="h-7 rounded-lg bg-fuchsia-500/85 px-2 text-[11px] hover:bg-fuchsia-500"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
                <div className="max-h-[280px] overflow-y-auto px-1 pb-1">
                  {presets.map((p) => (
                    <div
                      key={p.id}
                      data-testid={`fx-preset-row-${p.id}`}
                      className="group flex items-center gap-1.5 rounded-lg px-1.5 py-1 transition hover:bg-white/[0.06]"
                    >
                      <button
                        data-testid={`fx-preset-load-${p.id}`}
                        onClick={() => { onLoadPreset(p); toast.success(`Loaded "${p.name}" onto CH ${padCh(selectedChannel)}`); }}
                        className="flex flex-1 items-center gap-1.5 text-left"
                      >
                        {p.builtin
                          ? <Star className="h-3 w-3 flex-shrink-0 text-amber-300/80" />
                          : <span className="h-3 w-3 flex-shrink-0 rounded-full bg-fuchsia-500/60" />}
                        <span className="truncate text-[12px] text-white/90">{p.name}</span>
                        {p.builtin && <span className="ml-auto font-mono text-[9px] uppercase tracking-wider text-white/35">built-in</span>}
                      </button>
                      {!p.builtin && (
                        <button
                          data-testid={`fx-preset-delete-${p.id}`}
                          onClick={() => handleDeletePreset(p.id, p.name)}
                          className="grid h-5 w-5 place-items-center rounded-md text-white/30 transition hover:bg-rose-500/15 hover:text-rose-300"
                          title="Delete preset"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-white/45">
          Routing · CH {padCh(selectedChannel)} · live Web Audio · drag knobs, double-click to reset
        </p>

        {/* ─── Automation lane (iter 32) ────────────────────────────────────
            Arming this channel makes every knob movement during playback
            persist as a recorded automation point. Subsequent plays will
            replay the recorded curve on the underlying FX nodes (engine
            tickAutomation does the work each animation frame). */}
        <div
          data-testid="automation-row"
          className={`mb-3 flex items-center justify-between gap-2 rounded-2xl border px-3 py-2 transition ${
            automationArmed
              ? "border-rose-400/50 bg-gradient-to-r from-rose-500/15 via-fuchsia-500/10 to-rose-500/15"
              : "border-white/[0.08] bg-white/[0.025]"
          }`}
        >
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="automation-arm-btn"
              onClick={onToggleAutomationArm}
              aria-pressed={automationArmed}
              title={automationArmed
                ? "Recording knob movements on this channel during playback. Click to stop arming."
                : "Arm this channel — knob movements during playback get recorded for replay."}
              className={`relative grid h-7 w-7 place-items-center rounded-full border transition ${
                automationArmed
                  ? "border-rose-400/60 bg-rose-500/30 text-rose-100 shadow-[0_0_14px_rgba(244,63,94,0.5)]"
                  : "border-white/15 bg-white/[0.04] text-white/55 hover:border-rose-400/40 hover:bg-rose-500/15 hover:text-rose-100"
              }`}
            >
              {automationArmed && enginePlaying && (
                <span className="absolute inline-flex h-3.5 w-3.5 animate-ping rounded-full bg-rose-300/70" />
              )}
              <Circle className={`relative h-3 w-3 ${automationArmed ? "fill-current" : ""}`} />
            </button>
            <div className="flex flex-col leading-tight">
              <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${automationArmed ? "text-rose-100" : "text-white/65"}`}>
                Automation
              </span>
              <span data-testid="automation-status" className="font-mono text-[9px] uppercase tracking-wider text-white/45">
                {automationArmed
                  ? (enginePlaying ? "Recording knob movements…" : "Armed · press Play to capture")
                  : (automationSummary?.totalPoints
                      ? `${automationSummary.totalPoints} points across ${automationSummary.params.length} param${automationSummary.params.length === 1 ? "" : "s"}`
                      : "Off · knob drags don't record")}
              </span>
            </div>
          </div>
          {!!automationSummary?.totalPoints && (
            <button
              type="button"
              data-testid="automation-clear-btn"
              onClick={onClearAutomation}
              title="Clear all recorded automation on this channel"
              className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-white/65 transition hover:border-rose-400/30 hover:bg-rose-500/15 hover:text-rose-100"
            >
              <Eraser className="h-3 w-3" />Clear
            </button>
          )}
        </div>

        {/* Per-parameter automation lanes (iter 49) — final P3 item.
            One row per recorded (effect, param) pair with:
              - a tiny inline sparkline of the recorded curve
              - point count + effect badge
              - per-lane "X" clear button → drops just THAT lane
            Hidden when no automation exists OR while armed-and-recording
            (no point showing stale lanes mid-record). */}
        {automationLanes && automationLanes.length > 0 && !(automationArmed && enginePlaying) ? (
          <div
            data-testid="automation-lanes"
            className="mb-3 space-y-1 rounded-2xl border border-white/[0.07] bg-black/30 p-2"
          >
            <div className="px-1 pb-1 font-mono text-[9px] uppercase tracking-[0.18em] text-white/45">
              Per-param lanes ({automationLanes.length})
            </div>
            {automationLanes.map((lane) => {
              const laneKey = `${lane.name}:${lane.key}`;
              return (
                <div
                  key={laneKey}
                  data-testid={`automation-lane-${laneKey}`}
                  className="group flex items-center gap-2 rounded-lg px-2 py-1.5 transition hover:bg-white/[0.03]"
                >
                  <span className="w-10 flex-shrink-0 truncate rounded bg-fuchsia-500/15 px-1 py-0.5 text-center font-mono text-[8.5px] uppercase tracking-wider text-fuchsia-100">
                    {lane.name.slice(0, 4)}
                  </span>
                  <span className="flex-1 truncate font-mono text-[10.5px] text-white/75" title={lane.key}>
                    {lane.key}
                  </span>
                  <span className="text-fuchsia-200/80">
                    <LaneSparkline points={lane.points} min={lane.min} max={lane.max} />
                  </span>
                  <span className="w-9 flex-shrink-0 text-right font-mono text-[9px] tabular-nums text-white/45">
                    {lane.count}pt
                  </span>
                  <button
                    type="button"
                    data-testid={`automation-lane-clear-${laneKey}`}
                    onClick={() => onClearLaneParam?.(lane.name, lane.key)}
                    title={`Clear automation on ${lane.name} · ${lane.key}`}
                    aria-label={`Clear automation on ${lane.name} ${lane.key}`}
                    className="grid h-5 w-5 flex-shrink-0 place-items-center rounded text-white/35 opacity-0 transition hover:bg-rose-500/20 hover:text-rose-200 group-hover:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : null}

        <SidechainRow
          selectedChannel={selectedChannel}
          state={sidechainState}
          channelsWithAudio={channelsWithAudio}
          onChange={onSidechainChange}
        />

        <div className="space-y-2">
          {EFFECT_LIST.map(({ key, label, hint }) => {
            const active = !!selectedFx[key];
            const schema = FX_PARAM_SCHEMA[key];
            const params = fxParams?.[key] || {};
            const defaults = FX_PARAM_DEFAULTS[key] || {};
            return (
              <div
                key={`fx-row-${key}`}
                className={`overflow-hidden rounded-xl border transition ${
                  active ? "border-fuchsia-400/50 bg-fuchsia-500/[0.06]" : "border-white/10 bg-black/30"
                }`}
              >
                <label className="flex cursor-pointer items-center justify-between px-3 py-2 hover:bg-white/[0.03]">
                  <span className="flex flex-col">
                    <span className={`text-[13px] ${active ? "text-white" : "text-white/80"}`}>{label}</span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-white/40">{hint}</span>
                  </span>
                  <input
                    data-testid={`fx-${key}`}
                    type="checkbox"
                    checked={active}
                    onChange={() => onToggle(key)}
                    className="h-4 w-4 accent-fuchsia-400"
                  />
                </label>
                {/* Knob row — only visible when the effect is ON. Knobs DO still
                    write to engine.fxParams when bypassed, so a user can pre-dial
                    before flipping the switch. Hiding here is purely UX. */}
                {active && schema && (
                  <div
                    data-testid={`fx-${key}-knobs`}
                    className="flex flex-wrap items-start justify-around gap-3 border-t border-white/10 bg-black/30 px-3 py-3"
                  >
                    {Object.entries(schema).map(([paramKey, s]) => (
                      <Knob
                        key={`${key}-${paramKey}`}
                        testId={`fx-knob-${key}-${paramKey}`}
                        value={params[paramKey] ?? defaults[paramKey]}
                        defaultValue={defaults[paramKey]}
                        min={s.min}
                        max={s.max}
                        step={s.step}
                        unit={s.unit}
                        label={s.label}
                        onChange={(v) => onParamChange(key, paramKey, v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


/**
 * SidechainRow — compact UI for the per-channel sidechain ducker.
 *
 * 4 controls:
 *  - Trigger select   (which channel's audio drives the duck)
 *  - Threshold slider (0..1 — peak level above which ducking engages)
 *  - Depth slider     (0..1 — how much to duck, 0 = none, 1 = silence)
 *  - Quick Bypass on the trigger select → pick "None"
 *
 * Attack/Release stay at sensible defaults (5ms / 120ms) — exposed in the
 * engine API for future custom controls but not in the UI to keep the row
 * compact. Typical kick→bass pump effect doesn't need attack/release tuning.
 */
function SidechainRow({ selectedChannel, state, channelsWithAudio, onChange }) {
  const mode = state?.mode || "audio";
  const armed = mode === "lfo" || (mode === "audio" && state?.keyChannel != null);
  const threshold = state?.threshold ?? 0.30;
  const depth = state?.depth ?? 0.65;
  const subdivision = state?.lfoSubdivision ?? 1;

  if (!onChange) return null;

  return (
    <div
      data-testid="sidechain-row"
      className={`mb-3 rounded-2xl border px-3 py-2 transition ${
        armed
          ? "border-amber-400/45 bg-gradient-to-r from-amber-500/10 via-fuchsia-500/8 to-amber-500/10 shadow-[0_0_18px_rgba(252,211,77,0.18)]"
          : "border-white/[0.08] bg-white/[0.025]"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`grid h-7 w-7 place-items-center rounded-full border ${
          armed
            ? "border-amber-400/55 bg-amber-500/25 text-amber-100"
            : "border-white/15 bg-white/[0.04] text-white/55"
        }`}>
          <Zap className={`h-3 w-3 ${armed ? "fill-current" : ""}`} />
        </span>
        <div className="flex flex-col leading-tight">
          <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${armed ? "text-amber-100" : "text-white/65"}`}>
            Sidechain
          </span>
          <span className="font-mono text-[9px] uppercase tracking-wider text-white/45">
            {armed
              ? (mode === "lfo"
                  ? `Tempo-synced LFO · ${subdivision === 1 ? "1/4" : subdivision === 0.5 ? "1/8" : subdivision === 0.25 ? "1/16" : "1/2"} note · ${Math.round(depth * 100)}%`
                  : `Ducked by trigger · ${Math.round(depth * 100)}%`)
              : "Pick a trigger channel or switch to LFO mode"}
          </span>
        </div>
        {/* Mode toggle pill */}
        <div className="ml-auto flex items-center overflow-hidden rounded-lg border border-white/15 bg-black/40">
          <button
            type="button"
            data-testid="sidechain-mode-audio"
            onClick={() => onChange({ mode: "audio" })}
            className={`px-2 py-1 font-mono text-[9px] uppercase tracking-wider transition ${
              mode === "audio" ? "bg-amber-500/30 text-amber-100" : "text-white/55 hover:bg-white/[0.06]"
            }`}
          >
            Audio
          </button>
          <button
            type="button"
            data-testid="sidechain-mode-lfo"
            onClick={() => onChange({ mode: "lfo" })}
            className={`px-2 py-1 font-mono text-[9px] uppercase tracking-wider transition ${
              mode === "lfo" ? "bg-fuchsia-500/30 text-fuchsia-100" : "text-white/55 hover:bg-white/[0.06]"
            }`}
          >
            LFO
          </button>
        </div>
        {mode === "audio" ? (
          <select
            data-testid="sidechain-key-select"
            value={state?.keyChannel == null ? "" : String(state.keyChannel)}
            onChange={(e) => {
              const v = e.target.value;
              onChange({ keyChannel: v === "" ? null : Number(v) });
            }}
            className="rounded-lg border border-white/15 bg-black/40 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white focus:border-amber-400/60 focus:outline-none"
          >
            <option value="">None</option>
            {channelsWithAudio
              .filter((c) => c.idx !== selectedChannel)
              .map((c) => (
                <option key={`sc-opt-${c.idx}`} value={c.idx}>
                  {`CH ${String(c.idx + 1).padStart(2, "0")}${c.name ? " · " + c.name.replace(/\.[^.]+$/, "").slice(0, 18) : ""}`}
                </option>
              ))}
          </select>
        ) : (
          <select
            data-testid="sidechain-lfo-subdivision"
            value={String(subdivision)}
            onChange={(e) => onChange({ lfoSubdivision: Number(e.target.value) })}
            className="rounded-lg border border-white/15 bg-black/40 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white focus:border-fuchsia-400/60 focus:outline-none"
          >
            <option value="2">1/2 note</option>
            <option value="1">1/4 note</option>
            <option value="0.5">1/8 note</option>
            <option value="0.25">1/16 note</option>
          </select>
        )}
      </div>

      {armed && (
        <div className="mt-2 flex items-center gap-3">
          {mode === "audio" && (
            <div className="flex flex-1 flex-col gap-0.5">
              <label className="flex items-center justify-between font-mono text-[9px] uppercase tracking-wider text-white/55">
                <span>Threshold</span>
                <span data-testid="sidechain-threshold-value" className="tabular-nums text-amber-100">{threshold.toFixed(2)}</span>
              </label>
              <input
                data-testid="sidechain-threshold"
                type="range" min="0.02" max="0.95" step="0.01"
                value={threshold}
                onChange={(e) => onChange({ threshold: Number(e.target.value) })}
                className="studio-fader w-full"
                style={{ accentColor: "#fbbf24" }}
              />
            </div>
          )}
          <div className="flex flex-1 flex-col gap-0.5">
            <label className="flex items-center justify-between font-mono text-[9px] uppercase tracking-wider text-white/55">
              <span>Depth</span>
              <span data-testid="sidechain-depth-value" className="tabular-nums text-fuchsia-100">{Math.round(depth * 100)}%</span>
            </label>
            <input
              data-testid="sidechain-depth"
              type="range" min="0" max="1" step="0.01"
              value={depth}
              onChange={(e) => onChange({ depth: Number(e.target.value) })}
              className="studio-fader w-full"
              style={{ accentColor: "#e879f9" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
